import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Checkbox,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from '@mui/material';
import {
    Download as DownloadIcon,
    Block as BlockIcon,
    DeleteOutline as DeleteIcon,
    UploadFile as UploadFileIcon,
    ReceiptLong as ReceiptLongIcon,
    Refresh as RefreshIcon,
    RestartAlt as RestartAltIcon,
    Save as SaveIcon,
    Undo as UndoIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import masavService from '../../../Services/masav.service';
import ledgerReportsService from '../../../Services/ledgerReports.service';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import s from './MasavDraftModal.module.scss';

const BATCH_STATUS = {
    generated: 'Generated',
    applied: 'Applied',
    cancelled: 'Cancelled',
};

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '—');
const formatDateTime = (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—');
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function pickItem(raw) {
    return {
        id: raw.id ?? raw.Id,
        caseId: raw.caseId ?? raw.CaseId ?? '',
        clientName: raw.clientName ?? raw.ClientName ?? '',
        computedAmount: Number(raw.computedAmount ?? raw.ComputedAmount ?? 0),
        amount: Number(raw.amount ?? raw.Amount ?? 0),
        isCustomAmount: !!(raw.isCustomAmount ?? raw.IsCustomAmount),
        bankCode: raw.bankCode ?? raw.BankCode ?? '',
        branchNumber: raw.branchNumber ?? raw.BranchNumber ?? '',
        accountNumber: raw.accountNumber ?? raw.AccountNumber ?? '',
        beneficiaryId: raw.beneficiaryId ?? raw.BeneficiaryId ?? '',
        included: !!(raw.included ?? raw.Included),
        skipReason: raw.skipReason ?? raw.SkipReason ?? '',
        paymentDayOfMonth: raw.paymentDayOfMonth ?? raw.PaymentDayOfMonth ?? null,
        paymentDueDate: raw.paymentDueDate ?? raw.PaymentDueDate ?? null,
        paymentCreated: !!(raw.paymentCreated ?? raw.PaymentCreated),
        paymentIds: raw.paymentIds ?? raw.PaymentIds ?? [],
        receiptGenerated: !!(raw.receiptGenerated ?? raw.ReceiptGenerated),
        appliedAt: raw.appliedAt ?? raw.AppliedAt ?? null,
    };
}

function pickBatch(raw) {
    return {
        id: raw.id ?? raw.Id,
        startDate: raw.startDate ?? raw.StartDate ?? null,
        endDate: raw.endDate ?? raw.EndDate ?? null,
        monthOnly: !!(raw.monthOnly ?? raw.MonthOnly),
        paymentDate: raw.paymentDate ?? raw.PaymentDate ?? null,
        fileName: raw.fileName ?? raw.FileName ?? '',
        status: raw.status ?? raw.Status ?? BATCH_STATUS.generated,
        itemCount: Number(raw.itemCount ?? raw.ItemCount ?? 0),
        totalAmount: Number(raw.totalAmount ?? raw.TotalAmount ?? 0),
        registerDate: raw.registerDate ?? raw.RegisterDate ?? null,
        items: (raw.items ?? raw.Items ?? []).map(pickItem),
    };
}

function pickParsedRow(raw, i) {
    return {
        key: i,
        amount: Number(raw.amount ?? raw.Amount ?? 0),
        asmachta: raw.asmachta ?? raw.Asmachta ?? '',
        bankCode: raw.bankCode ?? raw.BankCode ?? '',
        branchNumber: raw.branchNumber ?? raw.BranchNumber ?? '',
        accountNumber: raw.accountNumber ?? raw.AccountNumber ?? '',
        beneficiaryId: raw.beneficiaryId ?? raw.BeneficiaryId ?? '',
        beneficiaryName: raw.beneficiaryName ?? raw.BeneficiaryName ?? '',
        matchedCaseId: raw.matchedCaseId ?? raw.MatchedCaseId ?? null,
        matchedClientName: raw.matchedClientName ?? raw.MatchedClientName ?? '',
        matchedItemId: raw.matchedItemId ?? raw.MatchedItemId ?? null,
        paymentAlreadyCreated: !!(raw.paymentAlreadyCreated ?? raw.PaymentAlreadyCreated),
    };
}

const toReceiptRow = (caseId, clientName, amount) => ({
    caseId,
    clientName,
    payableAmount: amount,
    netBalance: amount,
    isEligibleForDownload: true,
});

const MasavBatchHistoryTab = ({
    active,
    accountantId,
    direction,
    focusBatchId,
    reloadNonce = 0,
    onOpenBulkReceiptForSelection,
}) => {
    const { t } = useTranslation();
    const isRtl = direction === 'rtl';
    const [loading, setLoading] = useState(false);
    const [batches, setBatches] = useState([]);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [busyBatchId, setBusyBatchId] = useState(null);
    /** { [itemId]: string } — typed amounts not yet saved to the batch. */
    const [editedAmounts, setEditedAmounts] = useState({});
    const [savingAmounts, setSavingAmounts] = useState(false);
    const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
    const [parsing, setParsing] = useState(false);
    const [parseResult, setParseResult] = useState(null);
    const [selectedParsedKeys, setSelectedParsedKeys] = useState(() => new Set());
    const fileInputRef = useRef(null);

    const statusLabel = (status) => {
        if (status === BATCH_STATUS.applied) return t('MasavBatchStatusApplied');
        if (status === BATCH_STATUS.cancelled) return t('MasavBatchStatusCancelled');
        return t('MasavBatchStatusGenerated');
    };
    const statusChipClass = (status) => {
        if (status === BATCH_STATUS.applied) return `${s.chip} ${s.chipSuccess}`;
        if (status === BATCH_STATUS.cancelled) return `${s.chip} ${s.chipMuted}`;
        return s.chip;
    };

    const loadBatches = useCallback(async () => {
        if (!accountantId) return;
        setLoading(true);
        try {
            const data = await masavService.getMasavBatches(accountantId);
            setBatches((Array.isArray(data) ? data : []).map(pickBatch));
        } catch (e) {
            console.error(e);
            toast.error(e.message || t('Error'));
        } finally {
            setLoading(false);
        }
    }, [accountantId, t]);

    useEffect(() => {
        if (active) loadBatches();
    }, [active, reloadNonce, loadBatches]);

    useEffect(() => {
        if (focusBatchId) setSelectedBatchId(focusBatchId);
    }, [focusBatchId]);

    useEffect(() => {
        if (batches.length === 0) {
            setSelectedBatchId(null);
            return;
        }
        if (!selectedBatchId || !batches.some((b) => b.id === selectedBatchId)) {
            setSelectedBatchId(batches[0].id);
        }
    }, [batches, selectedBatchId]);

    const selectedBatch = useMemo(
        () => batches.find((b) => b.id === selectedBatchId) || null,
        [batches, selectedBatchId]
    );
    const isCancelled = selectedBatch?.status === BATCH_STATUS.cancelled;

    useEffect(() => {
        setEditedAmounts({});
        setSelectedItemIds(new Set());
        setParseResult(null);
        setSelectedParsedKeys(new Set());
    }, [selectedBatchId]);

    const replaceBatch = (raw) => {
        if (!raw) return null;
        const next = pickBatch(raw);
        setBatches((prev) => prev.map((b) => (b.id === next.id ? next : b)));
        return next;
    };

    const items = useMemo(() => selectedBatch?.items ?? [], [selectedBatch]);
    const includedItems = useMemo(() => items.filter((i) => i.included), [items]);
    const skippedItems = useMemo(() => items.filter((i) => !i.included), [items]);
    const canEditItem = (item) => !isCancelled && item.included && !item.paymentCreated;
    const selectableItems = useMemo(
        () => (isCancelled ? [] : includedItems.filter((i) => !i.paymentCreated)),
        [includedItems, isCancelled]
    );

    const amountFor = (item) => {
        const typed = editedAmounts[item.id];
        if (typed == null) return item.amount;
        const n = Number(typed);
        return Number.isFinite(n) ? n : item.amount;
    };

    const dirtyItems = useMemo(
        () =>
            includedItems.filter((item) => {
                const typed = editedAmounts[item.id];
                if (typed == null) return false;
                const n = Number(typed);
                return typed === '' || !Number.isFinite(n) || round2(n) !== round2(item.amount);
            }),
        [includedItems, editedAmounts]
    );
    const hasUnsaved = dirtyItems.length > 0;

    const draftTotal = useMemo(
        () => round2(includedItems.reduce((sum, i) => sum + amountFor(i), 0)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [includedItems, editedAmounts]
    );

    /** Returns the up-to-date batch on success, null on failure. */
    const saveAmounts = async () => {
        if (!selectedBatch) return null;
        if (!hasUnsaved) return selectedBatch;
        const invalid = dirtyItems.find((i) => {
            const n = Number(editedAmounts[i.id]);
            return editedAmounts[i.id] === '' || !Number.isFinite(n) || n <= 0;
        });
        if (invalid) {
            toast.error(t('MasavBatchInvalidAmount', { name: invalid.clientName || invalid.caseId }));
            return null;
        }
        setSavingAmounts(true);
        try {
            const payload = dirtyItems.map((i) => {
                const n = round2(Number(editedAmounts[i.id]));
                return { itemId: i.id, amount: n === round2(i.computedAmount) ? null : n };
            });
            const updated = await masavService.updateMasavBatchAmounts(accountantId, selectedBatch.id, payload);
            const next = replaceBatch(updated) || selectedBatch;
            setEditedAmounts({});
            toast.success(t('MasavBatchAmountsSaved'));
            return next;
        } catch (e) {
            console.error(e);
            toast.error(e.message || t('Error'));
            return null;
        } finally {
            setSavingAmounts(false);
        }
    };

    /** Resolves to the batch to continue with (saved or discarded), or null to abort. */
    const resolveUnsavedAmounts = async () => {
        if (!hasUnsaved) return selectedBatch;
        const result = await Swal.fire({
            title: t('MasavBatchUnsavedTitle'),
            text: t('MasavBatchUnsavedText'),
            icon: 'warning',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('MasavBatchSaveAndContinue'),
            denyButtonText: t('MasavBatchDiscardChanges'),
            cancelButtonText: t('Cancel'),
            reverseButtons: isRtl,
        });
        if (result.isConfirmed) return saveAmounts();
        if (result.isDenied) {
            setEditedAmounts({});
            return selectedBatch;
        }
        return null;
    };

    const handleSelectBatch = async (batchId) => {
        if (batchId === selectedBatchId) return;
        if (!(await resolveUnsavedAmounts())) return;
        setSelectedBatchId(batchId);
    };

    const handleDownloadBatch = async (batch) => {
        if (batch.id === selectedBatchId && !(await resolveUnsavedAmounts())) return;
        setBusyBatchId(batch.id);
        try {
            const { blob, fileName } = await masavService.downloadMasavBatch(accountantId, batch);
            await ledgerReportsService.DownloadFile(blob, fileName);
        } catch (e) {
            console.error(e);
            toast.error(e.message || t('Error'));
        } finally {
            setBusyBatchId(null);
        }
    };

    const confirmAction = (title, text) =>
        Swal.fire({
            title,
            text,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('Confirm'),
            cancelButtonText: t('Cancel'),
            reverseButtons: isRtl,
        }).then((r) => r.isConfirmed);

    const handleCancelBatch = async (batch) => {
        if (!(await confirmAction(t('MasavBatchCancelTitle'), t('MasavBatchCancelText')))) return;
        setBusyBatchId(batch.id);
        try {
            await masavService.cancelMasavBatch(accountantId, batch.id);
            toast.success(t('MasavBatchCancelled'));
            await loadBatches();
        } catch (e) {
            console.error(e);
            toast.error(e.message || t('Error'));
        } finally {
            setBusyBatchId(null);
        }
    };

    const handleDeleteBatch = async (batch) => {
        if (!(await confirmAction(t('MasavBatchDeleteTitle'), t('MasavBatchDeleteText')))) return;
        setBusyBatchId(batch.id);
        try {
            await masavService.deleteMasavBatch(accountantId, batch.id);
            toast.success(t('MasavBatchDeleted'));
            if (batch.id === selectedBatchId) setSelectedBatchId(null);
            await loadBatches();
        } catch (e) {
            console.error(e);
            toast.error(e.message || t('Error'));
        } finally {
            setBusyBatchId(null);
        }
    };

    const handleResetItem = async (item) => {
        if (!(await confirmAction(t('MasavBatchResetRowTitle'), t('MasavBatchResetRowText')))) return;
        try {
            const updated = await masavService.resetMasavBatchItem(accountantId, selectedBatch.id, item.id);
            replaceBatch(updated);
        } catch (e) {
            console.error(e);
            toast.error(e.message || t('Error'));
        }
    };

    const toggleItem = (id) => {
        setSelectedItemIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const allItemsSelected =
        selectableItems.length > 0 && selectableItems.every((i) => selectedItemIds.has(i.id));
    const toggleAllItems = () => {
        setSelectedItemIds(allItemsSelected ? new Set() : new Set(selectableItems.map((i) => i.id)));
    };
    const selectedItems = selectableItems.filter((i) => selectedItemIds.has(i.id));

    const handleRecordPayments = async () => {
        if (!selectedBatch || selectedItems.length === 0) return;
        const fresh = await resolveUnsavedAmounts();
        if (!fresh) return;
        const rows = selectedItems.map((i) => {
            const current = fresh.items.find((x) => x.id === i.id) || i;
            return toReceiptRow(current.caseId, current.clientName, current.amount);
        });
        onOpenBulkReceiptForSelection?.(rows, { batchId: fresh.id });
    };

    const handleParseFile = async (e) => {
        const file = e?.target?.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (!file || !selectedBatch) return;
        setParsing(true);
        setParseResult(null);
        try {
            const result = await masavService.parseMatchMasavBatchFile(accountantId, selectedBatch.id, file);
            const rows = (result?.rows ?? result?.Rows ?? []).map(pickParsedRow);
            setParseResult({
                rows,
                matchedCount: Number(result?.matchedCount ?? result?.MatchedCount ?? 0),
                unmatchedCount: Number(result?.unmatchedCount ?? result?.UnmatchedCount ?? 0),
                totalAmount: Number(result?.totalAmount ?? result?.TotalAmount ?? 0),
            });
            setSelectedParsedKeys(
                new Set(rows.filter((r) => r.matchedCaseId && !r.paymentAlreadyCreated).map((r) => r.key))
            );
        } catch (err) {
            console.error(err);
            toast.error(err.message || t('Error'));
        } finally {
            setParsing(false);
        }
    };

    const parsedRows = parseResult?.rows ?? [];
    const committableParsed = parsedRows.filter((r) => r.matchedCaseId && !r.paymentAlreadyCreated);
    const allParsedSelected =
        committableParsed.length > 0 && committableParsed.every((r) => selectedParsedKeys.has(r.key));
    const toggleParsed = (key) => {
        setSelectedParsedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleCommitParsed = () => {
        const chosen = committableParsed.filter((r) => selectedParsedKeys.has(r.key));
        if (chosen.length === 0) {
            toast.warning(t('MasavUploadNoMatches'));
            return;
        }
        const rows = chosen.map((r) =>
            toReceiptRow(r.matchedCaseId, r.matchedClientName || r.beneficiaryName, r.amount)
        );
        onOpenBulkReceiptForSelection?.(rows, { batchId: selectedBatch.id });
    };


    const renderItemStatus = (item) => {
        if (!item.included) {
            return <span className={`${s.chip} ${s.chipWarning} ${s.payDayChip}`}>{item.skipReason || t('MasavBatchSkipped')}</span>;
        }
        if (item.paymentCreated) {
            return (
                <div className={s.chipRow}>
                    <Tooltip title={item.appliedAt ? formatDateTime(item.appliedAt) : ''}>
                        <span className={`${s.chip} ${s.chipSuccess}`}>{t('MasavBatchPaid')}</span>
                    </Tooltip>
                    {item.receiptGenerated && <span className={s.chip}>{t('MasavBatchReceiptDrafted')}</span>}
                </div>
            );
        }
        return <span className={`${s.chip} ${s.chipMuted}`}>{t('MasavBatchAwaitingPayment')}</span>;
    };

    if (loading && batches.length === 0) {
        return (
            <div className={s.loading}>
                <CircularProgress className={s.spinner} />
            </div>
        );
    }

    if (batches.length === 0) {
        return (
            <div className={s.emptyState}>
                <HistoryIcon sx={{ fontSize: 36, opacity: 0.5, display: 'block', mx: 'auto', mb: 1 }} />
                {t('MasavBatchHistoryEmpty')}
            </div>
        );
    }

    return (
        <div>
            <div className={s.panelHeader}>
                <div>
                    <h3 className={s.sectionTitle}>{t('MasavBatchHistoryTitle')}</h3>
                    <p className={s.muted}>{t('MasavBatchHistoryHint')}</p>
                </div>
                <button type="button" className={s.ghostBtn} onClick={loadBatches} disabled={loading}>
                    {loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                    {t('Refresh')}
                </button>
            </div>

            <div className={s.batchList}>
                {batches.map((b) => {
                    const isActive = b.id === selectedBatchId;
                    const busy = busyBatchId === b.id;
                    const cancelled = b.status === BATCH_STATUS.cancelled;
                    const anyPaid = b.items.some((i) => i.paymentCreated);
                    return (
                        <div
                            key={b.id}
                            role="button"
                            tabIndex={0}
                            className={`${s.batchCard}${isActive ? ` ${s.batchCardActive}` : ''}${cancelled ? ` ${s.batchCardCancelled}` : ''}`}
                            onClick={() => handleSelectBatch(b.id)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSelectBatch(b.id);
                            }}
                        >
                            <div className={s.batchCardTop}>
                                <span className={s.batchCardTitle}>
                                    {t('MasavBatchPaymentDate')}: {formatDate(b.paymentDate)}
                                </span>
                                <span className={statusChipClass(b.status)}>{statusLabel(b.status)}</span>
                            </div>
                            <span className={s.batchCardMeta}>
                                {b.itemCount} • {beautifyNumber(b.totalAmount)} ₪
                            </span>
                            <div className={s.batchCardTop}>
                                <span className={s.batchCardMeta}>{formatDateTime(b.registerDate)}</span>
                                <div className={s.batchCardActions} onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title={t('MasavBatchDownload')}>
                                        <span>
                                            <IconButton size="small" disabled={busy || cancelled} onClick={() => handleDownloadBatch(b)}>
                                                {busy ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {!cancelled && (
                                        <Tooltip title={t('MasavBatchCancel')}>
                                            <span>
                                                <IconButton size="small" disabled={busy} onClick={() => handleCancelBatch(b)}>
                                                    <BlockIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <Tooltip title={anyPaid ? t('MasavBatchDeleteBlocked') : t('MasavBatchDelete')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                disabled={busy || anyPaid}
                                                onClick={() => handleDeleteBatch(b)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {selectedBatch && (
                <>
                    <div className={`${s.selectedBatchBar}${hasUnsaved ? ` ${s.unsavedBar}` : ''}`}>
                        <span className={s.sectionTitle}>
                            {t('MasavBatchRows')} ({includedItems.length})
                        </span>
                        <span className={s.footerTotals}>
                            {t('MasavTotals')}: {beautifyNumber(hasUnsaved ? draftTotal : selectedBatch.totalAmount)} ₪
                        </span>
                        {hasUnsaved && <span className={`${s.chip} ${s.chipWarning}`}>{t('MasavBatchUnsavedChip')}</span>}
                        <span className={s.footerSpacer} />
                        {hasUnsaved && (
                            <>
                                <button
                                    type="button"
                                    className={`${s.secondaryBtn} ${s.smallBtn}`}
                                    onClick={() => setEditedAmounts({})}
                                    disabled={savingAmounts}
                                >
                                    <UndoIcon fontSize="small" />
                                    {t('MasavBatchDiscardChanges')}
                                </button>
                                <button
                                    type="button"
                                    className={`${s.primaryBtn} ${s.smallBtn}`}
                                    onClick={saveAmounts}
                                    disabled={savingAmounts}
                                >
                                    {savingAmounts ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                                    {t('MasavBatchSaveAndRebuild')}
                                </button>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".txt,.001,.msv,text/plain,application/octet-stream"
                            style={{ display: 'none' }}
                            onChange={handleParseFile}
                        />
                        <button
                            type="button"
                            className={`${s.ghostBtn} ${s.smallBtn}`}
                            disabled={parsing || isCancelled}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {parsing ? <CircularProgress size={14} /> : <UploadFileIcon fontSize="small" />}
                            {t('MasavBatchUploadReturn')}
                        </button>
                        <button
                            type="button"
                            className={`${s.primaryBtn} ${s.smallBtn}`}
                            disabled={selectedItems.length === 0 || savingAmounts}
                            onClick={handleRecordPayments}
                        >
                            <ReceiptLongIcon fontSize="small" />
                            {t('MasavBatchRecordPayments')} ({selectedItems.length})
                        </button>
                    </div>
                    <div className={s.tableWrap} dir={direction} style={{ maxHeight: 420 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            checked={allItemsSelected}
                                            indeterminate={selectedItems.length > 0 && !allItemsSelected}
                                            onChange={toggleAllItems}
                                            disabled={selectableItems.length === 0}
                                        />
                                    </TableCell>
                                    <TableCell>{t('ClientName')}</TableCell>
                                    <TableCell>{t('Case')}</TableCell>
                                    <TableCell className={s.num}>{t('MasavBatchFullAmount')}</TableCell>
                                    <TableCell className={s.num}>{t('MasavBatchFileAmount')}</TableCell>
                                    <TableCell>{t('Bank')}</TableCell>
                                    <TableCell>{t('Branch')}</TableCell>
                                    <TableCell>{t('AccountNumber')}</TableCell>
                                    <TableCell>{t('Status')}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...includedItems, ...skippedItems].map((item) => {
                                    const editable = canEditItem(item);
                                    const typed = editedAmounts[item.id];
                                    const current = amountFor(item);
                                    const differsFromFull = round2(current) !== round2(item.computedAmount);
                                    return (
                                        <TableRow key={item.id} hover sx={!item.included ? { opacity: 0.75 } : undefined}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedItemIds.has(item.id)}
                                                    disabled={!item.included || item.paymentCreated || isCancelled}
                                                    onChange={() => toggleItem(item.id)}
                                                />
                                            </TableCell>
                                            <TableCell><span className={s.clientName}>{item.clientName}</span></TableCell>
                                            <TableCell><span className={s.clientCase}>{item.caseId}</span></TableCell>
                                            <TableCell className={s.num}>{beautifyNumber(item.computedAmount)}</TableCell>
                                            <TableCell className={s.num} sx={{ minWidth: 150 }}>
                                                {item.included ? (
                                                    editable ? (
                                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={typed ?? item.amount.toFixed(2)}
                                                                onChange={(e) =>
                                                                    setEditedAmounts((prev) => ({ ...prev, [item.id]: e.target.value }))
                                                                }
                                                                inputProps={{ min: 0, step: '0.01' }}
                                                                className={s.amountField}
                                                            />
                                                            {differsFromFull && (
                                                                <Tooltip title={t('MasavBatchResetToFull')}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            setEditedAmounts((prev) => ({
                                                                                ...prev,
                                                                                [item.id]: item.computedAmount.toFixed(2),
                                                                            }))
                                                                        }
                                                                    >
                                                                        <RestartAltIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontWeight: 600 }}>{beautifyNumber(item.amount)}</span>
                                                    )
                                                ) : (
                                                    '—'
                                                )}
                                                {item.included && differsFromFull && (
                                                    <span className={`${s.fullAmountHint}${current > item.computedAmount ? ` ${s.fullAmountHintOver}` : ''}`}>
                                                        {t('MasavBatchCustomAmount')}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>{item.bankCode}</TableCell>
                                            <TableCell>{item.branchNumber}</TableCell>
                                            <TableCell>{item.accountNumber}</TableCell>
                                            <TableCell sx={{ maxWidth: 260 }}>{renderItemStatus(item)}</TableCell>
                                            <TableCell>
                                                {item.paymentCreated && (
                                                    <Tooltip title={t('MasavBatchResetRowTitle')}>
                                                        <IconButton size="small" onClick={() => handleResetItem(item)}>
                                                            <UndoIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>

                    {parseResult && (
                        <div className={s.panel}>
                            <div className={s.panelHeader}>
                                <div className={s.chipRow}>
                                    <span className={s.sectionTitle}>{t('MasavBatchReturnFileTitle')}</span>
                                    <span className={`${s.chip} ${s.chipSuccess}`}>
                                        {t('MasavUploadMatched')}: {parseResult.matchedCount}
                                    </span>
                                    <span className={`${s.chip} ${s.chipWarning}`}>
                                        {t('MasavUploadUnmatched')}: {parseResult.unmatchedCount}
                                    </span>
                                    <span className={s.chip}>
                                        {t('MasavTotals')}: {beautifyNumber(parseResult.totalAmount)}
                                    </span>
                                </div>
                                <div className={s.chipRow}>
                                    <button
                                        type="button"
                                        className={`${s.secondaryBtn} ${s.smallBtn}`}
                                        onClick={() => setParseResult(null)}
                                    >
                                        {t('Close')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`${s.primaryBtn} ${s.smallBtn}`}
                                        disabled={selectedParsedKeys.size === 0}
                                        onClick={handleCommitParsed}
                                    >
                                        <ReceiptLongIcon fontSize="small" />
                                        {t('MasavUploadCommit')} ({selectedParsedKeys.size})
                                    </button>
                                </div>
                            </div>
                            <div className={s.tableWrap} dir={direction} style={{ maxHeight: 320 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={allParsedSelected}
                                                    indeterminate={selectedParsedKeys.size > 0 && !allParsedSelected}
                                                    onChange={() =>
                                                        setSelectedParsedKeys(
                                                            allParsedSelected ? new Set() : new Set(committableParsed.map((r) => r.key))
                                                        )
                                                    }
                                                    disabled={committableParsed.length === 0}
                                                />
                                            </TableCell>
                                            <TableCell>{t('MasavUploadMatchStatus')}</TableCell>
                                            <TableCell>{t('ClientName')}</TableCell>
                                            <TableCell>{t('Case')}</TableCell>
                                            <TableCell className={s.num}>{t('MasavPayableAmount')}</TableCell>
                                            <TableCell>{t('Bank')}</TableCell>
                                            <TableCell>{t('Branch')}</TableCell>
                                            <TableCell>{t('AccountNumber')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parsedRows.map((r) => (
                                            <TableRow key={`${r.asmachta}-${r.key}`} hover>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedParsedKeys.has(r.key)}
                                                        disabled={!r.matchedCaseId || r.paymentAlreadyCreated}
                                                        onChange={() => toggleParsed(r.key)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {!r.matchedCaseId ? (
                                                        <span className={`${s.chip} ${s.chipWarning}`}>{t('MasavUploadUnmatched')}</span>
                                                    ) : r.paymentAlreadyCreated ? (
                                                        <span className={`${s.chip} ${s.chipSuccess}`}>{t('MasavBatchPaid')}</span>
                                                    ) : (
                                                        <span className={s.chip}>{t('MasavUploadMatched')}</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>{r.matchedClientName || r.beneficiaryName}</TableCell>
                                                <TableCell className={s.clientCase}>{r.matchedCaseId || '—'}</TableCell>
                                                <TableCell className={s.num}>{beautifyNumber(r.amount)}</TableCell>
                                                <TableCell>{r.bankCode}</TableCell>
                                                <TableCell>{r.branchNumber}</TableCell>
                                                <TableCell>{r.accountNumber}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default MasavBatchHistoryTab;
